import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import Api from "../../../shared/api/api";
import { getSessionId } from "../../../shared/helpers/session";
import "../styles/ProductPage.css";

const SHIRT_SIZES = [
  "XS",
  "S",
  "M",
  "L",
  "XL",
  "2XL",
  "3XL",
  "4XL",
  "5XL",
];

const priceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function cleanLabel(value = "") {
  return String(value).replace(/\?/g, "").trim();
}

function normalizeValue(value = "") {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function looksLikeBackImage(url = "") {
  return /camera_label=back/i.test(url);
}

function looksLikeFrontImage(url = "") {
  return /camera_label=front/i.test(url) || !looksLikeBackImage(url);
}

function getProductImages(variant = {}, product = {}) {
  return {
    front:
      variant?.images?.front ||
      variant?.image_url ||
      product?.image_url ||
      null,

    back: variant?.images?.back || null,
  };
}

function readLocalCart() {
  try {
    const storedCart = JSON.parse(
      window.localStorage.getItem("cart") || "[]"
    );

    return Array.isArray(storedCart) ? storedCart : [];
  } catch (error) {
    console.error("Unable to read the local cart:", error);
    return [];
  }
}

function isApparelProduct(product) {
  const category = String(product?.category || "").toLowerCase();
  const title = String(product?.title || "").toLowerCase();

  return (
    category === "apparel" ||
    /shirt|tee|hoodie|sweatshirt/.test(title)
  );
}

function extractSizeAndColor(title = "", singleSizeOnly = false) {
  const cleanedTitle = String(title).trim();

  if (!cleanedTitle) {
    return {
      size: "",
      color: "",
    };
  }

  const parts = cleanedTitle
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);

  if (singleSizeOnly) {
    return {
      size: parts[0] || cleanedTitle,
      color: "",
    };
  }

  if (parts.length >= 2) {
    const first = parts[0];
    const second = parts.slice(1).join(" / ");

    const firstIsSize = SHIRT_SIZES.includes(first.toUpperCase());
    const secondIsSize = SHIRT_SIZES.includes(second.toUpperCase());

    if (firstIsSize && !secondIsSize) {
      return {
        size: first,
        color: second,
      };
    }

    if (!firstIsSize && secondIsSize) {
      return {
        size: second,
        color: first,
      };
    }

    return {
      size: first,
      color: second,
    };
  }

  return {
    size: cleanedTitle,
    color: "",
  };
}

function groupVariantRows(rows = []) {
  const variantsById = new Map();

  rows.forEach((row) => {
    const variantKey =
      row?.printify_variant_id ||
      row?.id ||
      `${row?.title || "variant"}-${row?.image_url || ""}`;

    const existingVariant = variantsById.get(variantKey) || {
      id: row?.id,
      merch_product_id: row?.merch_product_id,
      printify_variant_id: row?.printify_variant_id,
      title: row?.title || "",
      price: row?.price,
      image_url: row?.image_url || null,
      images: {
        front: null,
        back: null,
      },
    };

    const imageUrl = row?.image_url || "";

    if (imageUrl && looksLikeFrontImage(imageUrl)) {
      existingVariant.images.front = imageUrl;
    }

    if (imageUrl && looksLikeBackImage(imageUrl)) {
      existingVariant.images.back = imageUrl;
    }

    if (!existingVariant.image_url && imageUrl) {
      existingVariant.image_url = imageUrl;
    }

    variantsById.set(variantKey, existingVariant);
  });

  return Array.from(variantsById.values());
}

function sortSizes(firstSize, secondSize) {
  const firstIndex = SHIRT_SIZES.indexOf(
    String(firstSize).toUpperCase()
  );

  const secondIndex = SHIRT_SIZES.indexOf(
    String(secondSize).toUpperCase()
  );

  if (firstIndex !== -1 && secondIndex !== -1) {
    return firstIndex - secondIndex;
  }

  if (firstIndex !== -1) return -1;
  if (secondIndex !== -1) return 1;

  return String(firstSize).localeCompare(String(secondSize), undefined, {
    numeric: true,
  });
}

export default function ProductPage() {
  const { id } = useParams();
  const location = useLocation();

  const initialProduct = location.state?.product || null;

  const [product, setProduct] = useState(initialProduct);
  const [productStatus, setProductStatus] = useState(
    initialProduct ? "success" : "loading"
  );

  const [variants, setVariants] = useState([]);
  const [variantStatus, setVariantStatus] = useState("idle");

  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedVariant, setSelectedVariant] = useState(null);

  const [frontImage, setFrontImage] = useState(
    initialProduct?.image_url || null
  );
  const [backImage, setBackImage] = useState(null);

  const [frontLoaded, setFrontLoaded] = useState(false);
  const [backLoaded, setBackLoaded] = useState(false);

  const [cartStatus, setCartStatus] = useState("idle");
  const [cartMessage, setCartMessage] = useState("");

  const isShirt = useMemo(
    () => isApparelProduct(product),
    [product]
  );

  const isSingleSizeOnly = !isShirt;

  useEffect(() => {
    let cancelled = false;

    async function fetchProduct() {
      if (initialProduct) {
        setProduct(initialProduct);
        setFrontImage(initialProduct.image_url || null);
        setProductStatus("success");
        return;
      }

      try {
        setProductStatus("loading");

        const response = await Api.getMerchProducts();

        const products = Array.isArray(response)
          ? response
          : response?.products || response?.data || [];

        const matchingProduct = products.find(
          (currentProduct) =>
            String(currentProduct?.id) === String(id)
        );

        if (!matchingProduct) {
          throw new Error("Product not found.");
        }

        if (cancelled) return;

        setProduct(matchingProduct);
        setFrontImage(matchingProduct.image_url || null);
        setProductStatus("success");
      } catch (error) {
        if (cancelled) return;

        console.error("Error loading product:", error);
        setProduct(null);
        setProductStatus("error");
      }
    }

    fetchProduct();

    return () => {
      cancelled = true;
    };
  }, [id, initialProduct]);

  useEffect(() => {
    let cancelled = false;

    async function fetchVariants() {
      if (!product?.id) return;

      try {
        setVariantStatus("loading");

        const response = await Api.getMerchVariants(product.id);

        const rows = Array.isArray(response)
          ? response
          : response?.variants || response?.data || [];

        const groupedVariants = groupVariantRows(rows);

        if (cancelled) return;

        setVariants(groupedVariants);
        setSelectedSize("");
        setSelectedColor("");
        setSelectedVariant(null);

        const firstVariant = groupedVariants[0] || {};
        const images = getProductImages(firstVariant, product);

        setFrontImage(images.front);
        setBackImage(images.back);
        setFrontLoaded(false);
        setBackLoaded(false);

        setVariantStatus("success");
      } catch (error) {
        if (cancelled) return;

        console.error("Error loading product variants:", error);

        const images = getProductImages({}, product);

        setVariants([]);
        setSelectedSize("");
        setSelectedColor("");
        setSelectedVariant(null);
        setFrontImage(images.front);
        setBackImage(null);
        setVariantStatus("error");
      }
    }

    fetchVariants();

    return () => {
      cancelled = true;
    };
  }, [product]);

  const preparedVariants = useMemo(() => {
    return variants.map((variant) => {
      const optionValues = extractSizeAndColor(
        variant.title,
        isSingleSizeOnly
      );

      return {
        ...variant,
        optionSize: optionValues.size,
        optionColor: optionValues.color,
      };
    });
  }, [variants, isSingleSizeOnly]);

  const availableSizes = useMemo(() => {
    const sizes = new Set();

    preparedVariants.forEach((variant) => {
      if (variant.optionSize) {
        sizes.add(variant.optionSize);
      }
    });

    return Array.from(sizes).sort(sortSizes);
  }, [preparedVariants]);

  const availableColors = useMemo(() => {
    if (isSingleSizeOnly) return [];

    const colors = new Set();

    preparedVariants.forEach((variant) => {
      if (variant.optionColor) {
        colors.add(variant.optionColor);
      }
    });

    return Array.from(colors).sort((firstColor, secondColor) =>
      String(firstColor).localeCompare(String(secondColor))
    );
  }, [preparedVariants, isSingleSizeOnly]);

  const colorsBySize = useMemo(() => {
    const result = {};

    preparedVariants.forEach((variant) => {
      const { optionSize, optionColor } = variant;

      if (!optionSize || !optionColor) return;

      if (!result[optionSize]) {
        result[optionSize] = new Set();
      }

      result[optionSize].add(optionColor);
    });

    return Object.fromEntries(
      Object.entries(result).map(([size, colors]) => [
        size,
        Array.from(colors).sort((firstColor, secondColor) =>
          String(firstColor).localeCompare(String(secondColor))
        ),
      ])
    );
  }, [preparedVariants]);

  const sizesByColor = useMemo(() => {
    const result = {};

    preparedVariants.forEach((variant) => {
      const { optionSize, optionColor } = variant;

      if (!optionSize || !optionColor) return;

      if (!result[optionColor]) {
        result[optionColor] = new Set();
      }

      result[optionColor].add(optionSize);
    });

    return Object.fromEntries(
      Object.entries(result).map(([color, sizes]) => [
        color,
        Array.from(sizes).sort(sortSizes),
      ])
    );
  }, [preparedVariants]);

  const sizeOptions = useMemo(() => {
    if (isSingleSizeOnly || !selectedColor) {
      return availableSizes;
    }

    return sizesByColor[selectedColor] || availableSizes;
  }, [
    availableSizes,
    isSingleSizeOnly,
    selectedColor,
    sizesByColor,
  ]);

  const colorOptions = useMemo(() => {
    if (!selectedSize) {
      return availableColors;
    }

    return colorsBySize[selectedSize] || availableColors;
  }, [availableColors, colorsBySize, selectedSize]);

  const priceDisplay =
    selectedVariant?.price ?? product?.price ?? null;

  function updateSelectedVariant(size, color) {
    const normalizedSize = normalizeValue(size);
    const normalizedColor = normalizeValue(color);

    const matchingVariant =
      preparedVariants.find((variant) => {
        const variantSize = normalizeValue(variant.optionSize);
        const variantColor = normalizeValue(variant.optionColor);

        if (isSingleSizeOnly) {
          return variantSize === normalizedSize;
        }

        return (
          variantSize === normalizedSize &&
          variantColor === normalizedColor
        );
      }) || null;

    setSelectedVariant(matchingVariant);

    if (!matchingVariant) {
      return;
    }

    const images = getProductImages(matchingVariant, product);

    setFrontLoaded(false);
    setBackLoaded(false);
    setFrontImage(images.front);
    setBackImage(images.back);
  }

  function handleSizeChange(event) {
    const size = event.target.value;

    let color = selectedColor;

    if (
      color &&
      !(colorsBySize[size] || []).includes(color)
    ) {
      color = "";
    }

    setSelectedSize(size);
    setSelectedColor(color);
    setCartMessage("");

    updateSelectedVariant(size, color);
  }

  function handleColorChange(event) {
    const color = event.target.value;

    let size = selectedSize;

    if (
      size &&
      !(sizesByColor[color] || []).includes(size)
    ) {
      size = "";
    }

    setSelectedColor(color);
    setSelectedSize(size);
    setCartMessage("");

    updateSelectedVariant(size, color);
  }

  async function handleAddToCart() {
    if (!product || !selectedVariant) {
      setCartStatus("error");
      setCartMessage("Please select the available product options.");
      return;
    }

    const sessionId = getSessionId();

    if (!sessionId) {
      setCartStatus("error");
      setCartMessage(
        "A shopping session could not be created. Please refresh the page."
      );
      return;
    }

    const cartItem = {
      item_type: "merch",
      merch_product_id: product.id,
      merch_variant_id: selectedVariant.id,
      title: product.title,
      image_url: frontImage || product.image_url || null,
      variant_title: selectedVariant.title,
      price: selectedVariant.price,
      qty: 1,
    };

    try {
      setCartStatus("loading");
      setCartMessage("");

      await Api.addToCart({
        item_type: cartItem.item_type,
        sessionId,
        product_id: cartItem.merch_product_id,
        variant_id: cartItem.merch_variant_id,
        variant_title: cartItem.variant_title,
        quantity: cartItem.qty,
        price_at_time: cartItem.price,
        image_url: cartItem.image_url,
        merch_product_id: cartItem.merch_product_id,
        merch_variant_id: cartItem.merch_variant_id,
      });

      const existingCart = readLocalCart();

      const existingItemIndex = existingCart.findIndex(
        (item) =>
          String(item?.merch_variant_id) ===
          String(cartItem.merch_variant_id)
      );

      let updatedCart;

      if (existingItemIndex >= 0) {
        updatedCart = [...existingCart];

        updatedCart[existingItemIndex] = {
          ...updatedCart[existingItemIndex],
          qty:
            Number(updatedCart[existingItemIndex]?.qty || 0) + 1,
        };
      } else {
        updatedCart = [...existingCart, cartItem];
      }

      window.localStorage.setItem(
        "cart",
        JSON.stringify(updatedCart)
      );

      window.dispatchEvent(new Event("cart-updated"));

      setCartStatus("success");
      setCartMessage(`${product.title} was added to your cart.`);
    } catch (error) {
      console.error("Failed to add product to cart:", error);

      setCartStatus("error");
      setCartMessage(
        error?.message ||
          "The product could not be added to your cart."
      );
    }
  }

  if (productStatus === "loading") {
    return (
      <main className="product-page">
        <p className="loadingMsg" role="status">
          Loading product...
        </p>
      </main>
    );
  }

  if (productStatus === "error") {
    return (
      <main className="product-page">
        <p className="warningError" role="alert">
          This product could not be loaded.
        </p>

        <Link to="/shop" className="btn btn--secondary">
          Back to Shop
        </Link>
      </main>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <main className="product-page">
      <nav
        className="product-page__topbar"
        aria-label="Product navigation"
      >
        <Link to="/shop" className="btn btn--secondary">
          ← Back to Shop
        </Link>
      </nav>

      <div className="product-3col">
        <section
          className="product-col product-col--images"
          aria-label={`${product.title} product images`}
        >
          <div className="product-media">
            {frontImage ? (
              <figure className="product-media__frame">
                <img
                  src={frontImage}
                  alt={`${product.title}${isShirt ? " — front" : ""}`}
                  className={`product-media__img ${
                    frontLoaded ? "loaded" : ""
                  }`}
                  onLoad={() => setFrontLoaded(true)}
                  onError={() => setFrontLoaded(true)}
                  decoding="async"
                />

                {isShirt && (
                  <figcaption className="product-media__label">
                    Front
                  </figcaption>
                )}
              </figure>
            ) : (
              <div className="product-media__frame">
                <p>No product image is currently available.</p>
              </div>
            )}

            {isShirt && backImage && (
              <figure className="product-media__frame">
                <img
                  src={backImage}
                  alt={`${product.title} — back`}
                  className={`product-media__img ${
                    backLoaded ? "loaded" : ""
                  }`}
                  onLoad={() => setBackLoaded(true)}
                  onError={() => setBackLoaded(true)}
                  loading="lazy"
                  decoding="async"
                />

                <figcaption className="product-media__label">
                  Back
                </figcaption>
              </figure>
            )}
          </div>
        </section>

        <section
          className="product-col product-col--details"
          aria-labelledby="product-title"
        >
          <h1 id="product-title" className="product-title">
            {product.title || "Product"}
          </h1>

          {product.description ? (
            <div
              className="product-description"
              dangerouslySetInnerHTML={{
                __html: product.description,
              }}
            />
          ) : (
            <p className="product-description">
              Product description coming soon.
            </p>
          )}
        </section>

        <section
          className="product-col product-col--options"
          aria-label="Product options"
        >
          {variantStatus === "loading" && (
            <p role="status">Loading product options...</p>
          )}

          {variantStatus === "error" && (
            <p className="warningError" role="alert">
              Product options could not be loaded.
            </p>
          )}

          {variantStatus === "success" &&
            preparedVariants.length === 0 && (
              <p role="alert">
                This product currently has no available variants.
              </p>
            )}

          {preparedVariants.length > 0 && (
            <>
              <div className="field">
                <label htmlFor="product-size">
                  <strong>Choose a size:</strong>
                </label>

                <select
                  id="product-size"
                  value={selectedSize}
                  onChange={handleSizeChange}
                >
                  <option value="">Select Size</option>

                  {sizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {cleanLabel(size)}
                    </option>
                  ))}
                </select>
              </div>

              {!isSingleSizeOnly && (
                <div className="field">
                  <label htmlFor="product-color">
                    <strong>Choose a color:</strong>
                  </label>

                  <select
                    id="product-color"
                    value={selectedColor}
                    onChange={handleColorChange}
                  >
                    <option value="">Select Color</option>

                    {colorOptions.map((color) => (
                      <option key={color} value={color}>
                        {cleanLabel(color)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {priceDisplay != null && (
                <p className="product-price">
                  {priceFormatter.format(Number(priceDisplay))}
                </p>
              )}

              <button
                type="button"
                className="btn btn--primary"
                onClick={handleAddToCart}
                disabled={
                  !selectedVariant || cartStatus === "loading"
                }
              >
                {cartStatus === "loading"
                  ? "Adding..."
                  : "Add to Cart"}
              </button>

              {cartMessage && (
                <p
                  className={`product-cart-message ${cartStatus}`}
                  role={
                    cartStatus === "error" ? "alert" : "status"
                  }
                  aria-live="polite"
                >
                  {cartMessage}
                </p>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}